/**
 * Formatage de dates et grille de calendrier, par défaut en français.
 *
 * Vit dans le noyau, et non dans une application : le web et le mobile dessinent le même
 * calendrier, et deux grilles de mois écrites séparément finiraient par ne pas placer le
 * même jour dans la même case. Pur, sans I/O — la règle de `custody/` (T-010).
 *
 * Repose sur `Intl.DateTimeFormat` plutôt que sur des tables de mois et de jours écrites
 * à la main : la plateforme sait déjà décliner un mois ou un jour de semaine dans une
 * langue donnée, avec le bon ordre des mots et la bonne casse — écrire une table par
 * langue aurait recopié ce que `Intl` fait déjà, avec le risque d'y semer des variantes.
 * Vérifié une fois (`apps/web/test/formatage-dates.test.ts`) : le rendu français d'`Intl` est identique, lettre
 * pour lettre, à l'ancienne table qu'il remplace ici — sauf `heure()`, qui garde sa forme
 * « 16h30 » propre au français, gardée pour ne rien changer aux affichages existants qui
 * n'ont pas encore de langue à passer.
 */
export const NOMS_JOURS_COURTS = ['lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.', 'dim.'];
/**
 * Les sept jours courts, dans l'ordre lundi → dimanche, dans la langue demandée.
 * Le 5 janvier 2026 est un lundi : sept jours consécutifs à partir de cette date
 * couvrent donc exactement une semaine, dans le bon ordre.
 */
export function nomsJoursCourts(locale = 'fr') {
    const format = new Intl.DateTimeFormat(locale, { weekday: 'short' });
    return Array.from({ length: 7 }, (_, i) => format.format(new Date(2026, 0, 5 + i)));
}
/**
 * Nombre de jours calendaires pleins écoulés depuis `date` — 0 le jour même, 1 la veille.
 *
 * Compare des minuits, pas des instants : deux horodatages à 23h50 et 00h10 le lendemain
 * ne sont séparés que de vingt minutes, mais appartiennent déjà à deux jours différents,
 * et c'est ce que « depuis hier » doit dire.
 */
export function joursDepuis(date, maintenant) {
    const debut = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const fin = new Date(maintenant.getFullYear(), maintenant.getMonth(), maintenant.getDate());
    return Math.round((fin.getTime() - debut.getTime()) / 86_400_000);
}
export const jourEtMois = (date, locale = 'fr') => new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long' }).format(date);
export const dateComplete = (date, locale = 'fr') => new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
}).format(date);
export const moisEtAnnee = (date, locale = 'fr') => new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(date);
export const heure = (date, locale = 'fr') => {
    if (locale === 'fr') {
        return `${String(date.getHours()).padStart(2, '0')}h${String(date.getMinutes()).padStart(2, '0')}`;
    }
    return new Intl.DateTimeFormat(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    }).format(date);
};
/** Clé de jour local (AAAA-MM-JJ), utilisée pour indexer un calendrier. */
export const cleDuJour = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
/**
 * Grille du mois, en semaines commençant le lundi.
 * Les cases hors du mois sont nulles : le calendrier ne déborde pas sur les mois voisins,
 * ce qui éviterait de laisser croire qu'une période y est déjà arbitrée.
 */
export function grilleDuMois(annee, mois) {
    const premier = new Date(annee, mois, 1);
    const nbJours = new Date(annee, mois + 1, 0).getDate();
    const decalage = (premier.getDay() + 6) % 7;
    const cases = Array.from({ length: decalage }, () => null);
    for (let jour = 1; jour <= nbJours; jour += 1)
        cases.push(new Date(annee, mois, jour));
    while (cases.length % 7 !== 0)
        cases.push(null);
    const semaines = [];
    for (let i = 0; i < cases.length; i += 7)
        semaines.push(cases.slice(i, i + 7));
    return semaines;
}
/** Le jour suivant, en clé de jour local. */
export const lendemainDuJour = (jour) => {
    const date = new Date(`${jour}T00:00:00`);
    date.setDate(date.getDate() + 1);
    return cleDuJour(date);
};
/**
 * Regroupe des jours choisis un à un en séries consécutives.
 *
 * Un échange de jours se demande rarement sur une seule case : c'est un week-end, deux
 * jours de pont, ou trois jours pris à deux endroits du mois. Le parent coche les jours
 * qui l'arrangent ; c'est ici qu'ils redeviennent des **périodes**, la seule forme que
 * l'API connaisse (`lirePeriodes`, côté serveur).
 *
 * Deux jours qui se suivent forment une période, deux jours séparés en forment deux : une
 * proposition « samedi et le samedi suivant » ne doit pas emporter la semaine entre les
 * deux, ce qu'une période unique du premier au dernier ferait sans le dire.
 *
 * Les doublons sont écartés et l'ordre est rétabli : la grille peut cocher dans n'importe
 * quel sens, la sortie est toujours chronologique.
 */
export function grouperJoursConsecutifs(jours) {
    const tries = [...new Set(jours)].sort();
    const series = [];
    for (const jour of tries) {
        const serie = series[series.length - 1];
        const dernier = serie?.[serie.length - 1];
        if (serie && dernier && lendemainDuJour(dernier) === jour)
            serie.push(jour);
        else
            series.push([jour]);
    }
    return series;
}
//# sourceMappingURL=calendrier.js.map